import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import ContentWrapper from '../components/contentwrapper/ContentWrapper';
import { Link } from 'react-router-dom';
import { fetchAllDataAPI, addFormData } from '../utils/api';
import { toast } from 'react-toastify';
import { FaFilter, FaRedo } from 'react-icons/fa';
import { CiExport } from 'react-icons/ci';
import CsvDownloader from 'react-csv-downloader';
import {
    MdLocalShipping, MdDoneAll, MdCancel,
    MdPendingActions, MdNewLabel
} from 'react-icons/md';
import withAuth from '../utils/withAuth';

const STATUS_CONFIG = {
    'Delivered': { color: '#0e7c52', bg: '#e8f5e9' },
    'RTO Delivered': { color: '#597e0d', bg: '#f1f8e9' },
    'In Transit': { color: '#1565c0', bg: '#e3f2fd' },
    'Out For Delivery': { color: '#00838f', bg: '#e0f7fa' },
    'Cancelled': { color: '#c62828', bg: '#ffebee' },
    'Failed Delivery': { color: '#c62828', bg: '#ffebee' },
    'NEW': { color: '#6a1b9a', bg: '#f3e5f5' },
    'Pickup Booked': { color: '#4e9191', bg: '#e0f4f4' },
    'default': { color: '#555', bg: '#f5f5f5' },
};

const TABS = [
    { key: 'all', label: 'All', icon: <MdLocalShipping />, color: '#4e9191' },
    { key: 'new', label: 'New', icon: <MdNewLabel />, color: '#6a1b9a' },
    { key: 'delivered', label: 'Delivered', icon: <MdDoneAll />, color: '#0e7c52' },
    { key: 'process', label: 'Processing', icon: <MdPendingActions />, color: '#1565c0' },
    { key: 'cancelled', label: 'Cancelled', icon: <MdCancel />, color: '#c62828' },
];

const SkeletonRow = () => (
    <tr>{[...Array(13)].map((_, i) => (
        <td key={i}><div style={{ height: 14, borderRadius: 6, background: 'linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }} /></td>
    ))}</tr>
);

const getStatusStyle = (s) => STATUS_CONFIG[s] || STATUS_CONFIG['default'];

const SkinrangeOrders = () => {
    const ref = useRef();
    const [orders, setOrders] = useState([]);
    const [counts, setCounts] = useState({});
    const [isLoading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('all');
    const [filters, setFilters] = useState({});
    const [currentPage, setPage] = useState(1);
    const perPage = 100;

    const filtered = orders.filter(o => {
        if (activeTab === 'all') return true;
        if (activeTab === 'new') return !o.shipping_status || o.shipping_status === '' || o.shipping_status === 'NEW';
        if (activeTab === 'delivered') return ['Delivered', 'RTO Delivered'].includes(o.shipping_status);
        if (activeTab === 'process') return ['In Transit', 'Out For Delivery', 'Pickup Booked', 'RTO Initiated', 'Pickup Rescheduled', 'Out For Pickup', 'Delayed', 'In Flight', 'Box Packing', 'Ready To Pack', 'PICKUP SCHEDULED', 'Pickup Generated', 'Picklist Generated'].includes(o.shipping_status);
        if (activeTab === 'cancelled') return ['Cancelled', 'Failed Delivery', 'Returned'].includes(o.shipping_status);
        return true;
    });

    const npage = Math.ceil(filtered.length / perPage);
    const records = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);
    const numbers = [...Array(npage + 1).keys()].slice(1);

    useEffect(() => { loadOrders(); loadCounts(); }, []);

    const loadOrders = () => {
        setLoading(true);
        fetchAllDataAPI('sub-agent-skinrange-orders')
            .then(res => { if (res?.data?.status === 1) setOrders(res.data.orders || []); })
            .catch(() => toast.error('Failed to load orders'))
            .finally(() => setLoading(false));
    };

    const loadCounts = () => {
        fetchAllDataAPI('sub-agent-skinrange-counts')
            .then(res => { if (res?.data?.status === 1) setCounts(res.data.counts || {}); });
    };

    const handleFilter = (e) => {
        e.preventDefault();
        if (Object.keys(filters).some(k => filters[k])) {
            setLoading(true);
            addFormData('sub-agent-skinrange-filter', filters)
                .then(res => { if (res?.data?.status === 1) { setOrders(res.data.orders || []); setPage(1); } })
                .catch(() => toast.error('Filter failed'))
                .finally(() => setLoading(false));
        } else {
            loadOrders();
        }
    };

    const reset = () => { ref.current.reset(); setFilters({}); loadOrders(); };
    const handleInput = e => setFilters({ ...filters, [e.target.name]: e.target.value });

    const csvCols = [
        { id: 'order_number', displayName: 'Order No' },
        { id: 'order_name', displayName: 'Name' },
        { id: 'order_mobile', displayName: 'Mobile' },
        { id: 'state', displayName: 'State' },
        { id: 'city', displayName: 'City' },
        { id: 'shipping_status', displayName: 'Status' },
        { id: 'awb_number', displayName: 'AWB' },
        { id: 'ocourier_name', displayName: 'Courier' },
        { id: 'order_confirm_status', displayName: 'Confirm Status' },
        { id: 'created_at', displayName: 'Order Date' },
    ];

    return (
        <div className="misDashboard">
            <Helmet><title>SATKARTAR::SKINRANGE ORDERS</title></Helmet>
            <style>{`
                @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
                @keyframes fadeInUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
                .sk-orders-page {
                    animation: fadeInUp 0.4s ease;
                    overflow-x: hidden; 
                }

                /* Hero */
                .sk-hero {
                    background: linear-gradient(135deg, #0d4f4f 0%, #4e9191 100%);
                    border-radius: 16px; padding: 20px 24px; color: #fff;
                    margin-bottom: 20px; box-shadow: 0 8px 24px rgba(78,145,145,0.25);
                    display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px;
                }

                /* Tabs */
                .sk-tabs { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 18px; }
                .sk-tab {
                    display: flex; align-items: center; gap: 8px;
                    padding: 9px 18px; border-radius: 50px; border: 2px solid #eee;
                    background: #fff; cursor: pointer; font-size: 13px; font-weight: 600;
                    transition: all 0.2s; box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                }
                .sk-tab:hover { transform: translateY(-2px); box-shadow: 0 4px 14px rgba(0,0,0,0.1); }
                .sk-tab.active { color: #fff !important; border-color: transparent; }
                .sk-tab-count { background: rgba(255,255,255,0.25); padding: 1px 8px; border-radius: 20px; font-size: 11px; font-weight: 700; }
                .sk-tab:not(.active) .sk-tab-count { background: #f0f0f0; color: #555; }

                /* Filter */
                .sk-filter {
                    background: #fff;
                    border-radius: 16px;
                    padding: 18px 20px;
                    margin-bottom: 18px;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.06);
                    overflow: hidden; 
                }
                .sk-filter label { font-size: 11px; font-weight: 600; color: #888; text-transform: uppercase; letter-spacing: 0.5px; display: block; margin-bottom: 5px; }
                .sk-filter .form-control { border-radius: 10px; border: 1.5px solid #eee; font-size: 13px; padding: 8px 12px; }
                .sk-filter .form-control:focus { border-color: #4e9191; box-shadow: 0 0 0 3px rgba(78,145,145,0.1); outline: none; }

                /* Table */
                .sk-table-card {
                    background: #fff;
                    border-radius: 16px;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.06);
                    overflow: hidden;
                    margin-bottom: 50px;
                }.sk-table-card 
                .table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
                .sk-table-card table { margin: 0; min-width: 1200px; }
                .sk-table-card thead th { background: #f8f9fa; font-size: 11px; font-weight: 700; color: #666; text-transform: uppercase; letter-spacing: 0.5px; padding: 13px 12px; border-bottom: 2px solid #eee; white-space: nowrap; }
                .sk-table-card tbody tr:hover { background: #f8fffe; }
                .sk-table-card tbody td { font-size: 13px; color: #444; padding: 11px 12px; vertical-align: middle; white-space: nowrap; }

                /* Pagination */
                .round-pagination .page-item.actives .page-link { background: #4e9191 !important; border-color: #4e9191 !important; color: #fff !important; border-radius: 8px !important; }
                .round-pagination .page-item .page-link { background: #fff; border: 1.5px solid #eee; color: #555; border-radius: 8px !important; margin: 0 2px; font-size: 12px; font-weight: 600; padding: 5px 12px; box-shadow: none !important; }
                .round-pagination .page-item .page-link:hover { background: #4e9191 !important; color: #fff !important; }
            `}</style>

            <ContentWrapper>
                <div className="sk-orders-page">

                    {/* Breadcrumb */}
                    <div className="page-breadcrumb d-none d-sm-flex align-items-center mb-3">
                        <nav><ol className="breadcrumb mb-0 p-0">
                            <li className="breadcrumb-item"><Link to="/dashboard">Home</Link></li>
                            <li className="breadcrumb-item">Skinrange Orders</li>
                        </ol></nav>
                    </div>

                    {/* Hero */}
                    <div className="sk-hero">
                        <div>
                            <div style={{ fontSize: 11, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 1 }}>Skinrange Platform</div>
                            <div style={{ fontSize: 26, fontWeight: 800 }}>{orders.length} Orders</div>
                            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>Center ID: 99</div>
                        </div>
                        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: 11, opacity: 0.7 }}>Delivered</div>
                                <div style={{ fontSize: 22, fontWeight: 800 }}>{counts.delivered || 0}</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: 11, opacity: 0.7 }}>Processing</div>
                                <div style={{ fontSize: 22, fontWeight: 800 }}>{counts.process || 0}</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: 11, opacity: 0.7 }}>Cancelled</div>
                                <div style={{ fontSize: 22, fontWeight: 800 }}>{counts.cancelled || 0}</div>
                            </div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="sk-tabs">
                        {TABS.map(tab => (
                            <div key={tab.key}
                                className={`sk-tab ${activeTab === tab.key ? 'active' : ''}`}
                                style={activeTab === tab.key ? { background: tab.color, color: '#fff' } : { color: tab.color }}
                                onClick={() => { setActiveTab(tab.key); setPage(1); }}>
                                {tab.icon} {tab.label}
                                <span className="sk-tab-count">{counts[tab.key] ?? '—'}</span>
                            </div>
                        ))}
                    </div>

                    {/* Filter */}
                    <div className="sk-filter">
                        <form ref={ref} onSubmit={handleFilter}>
                            {/* Filter row - fix col sizes to fit inside card */}
                            <div className="row g-2 align-items-end">
                                <div className="col-6 col-lg-2">
                                    <label>Search By Name</label>
                                    <input name="cname" className="form-control" type="text" placeholder="Customer name" onChange={handleInput} />
                                </div>
                                <div className="col-6 col-lg-2">
                                    <label>Order Number</label>
                                    <input name="onumber" className="form-control" type="text" placeholder="Order number" onChange={handleInput} />
                                </div>
                                <div className="col-6 col-lg-2">
                                    <label>Mobile Number</label>
                                    <input name="cmobile" className="form-control" type="text" placeholder="Mobile number" onChange={handleInput} />
                                </div>
                                <div className="col-6 col-lg-2">
                                    <label>Order Status</label>
                                    <select name="sstatus" className="form-control" onChange={handleInput}>
                                        <option value="">All Statuses</option>
                                        {['NEW', 'Pickup Booked', 'In Transit', 'Out For Delivery', 'Delivered',
                                            'RTO Initiated', 'RTO Delivered', 'Cancelled', 'Failed Delivery', 'Returned'].map(s => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                    </select>
                                </div>
                                <div className="col-6 col-lg-2">
                                    <label>Date From</label>
                                    <input name="date_from" className="form-control" type="date" onChange={handleInput} />
                                </div>
                                <div className="col-6 col-lg-2">
                                    <label>Date To</label>
                                    <input name="date_to" className="form-control" type="date" onChange={handleInput} />
                                </div>

                                {/* Buttons on new row — full width, stays inside card */}
                                <div className="col-12">
                                    <div className="d-flex gap-2 align-items-center flex-wrap" style={{ paddingTop: 8 }}>
                                        <button type="submit" className="btn btn-sm"
                                            style={{ background: '#4e9191', color: '#fff', borderRadius: 8, fontWeight: 600, fontSize: 12, padding: '7px 18px' }}>
                                            <FaFilter style={{ marginRight: 4, fontSize: 10 }} /> Filter
                                        </button>
                                        <button type="button" onClick={reset} className="btn btn-sm"
                                            style={{ background: '#fff3cd', color: '#856404', borderRadius: 8, fontWeight: 600, fontSize: 12, padding: '7px 14px' }}>
                                            <FaRedo style={{ marginRight: 4, fontSize: 11 }} /> Reset
                                        </button>
                                        <CsvDownloader filename="Skinrange_Orders" extension=".csv" datas={orders} columns={csvCols}>
                                            <button type="button" className="btn btn-sm"
                                                style={{ background: '#e3f2fd', color: '#1565c0', borderRadius: 8, fontWeight: 600, fontSize: 12, padding: '7px 14px' }}>
                                                <CiExport style={{ marginRight: 4, fontSize: 14 }} /> Export
                                            </button>
                                        </CsvDownloader>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>

                    {/* Table */}
                    <div className="sk-table-card">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                            <span style={{ fontSize: 13, color: '#666', fontWeight: 500 }}>
                                Showing <strong>{records.length}</strong> of <strong>{filtered.length}</strong> orders
                            </span>
                            <span style={{ fontSize: 13, color: '#666' }}>Page <strong>{currentPage}</strong> of <strong>{npage || 1}</strong></span>
                        </div>

                        <div className="table-scroll">
                            <table className="table table-hover mb-0">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Order No.</th>
                                        <th>AWB Number</th>
                                        <th>Name</th>
                                        <th>Mobile</th>
                                        <th>State</th>
                                        <th>City</th>
                                        <th>Pincode</th>
                                        <th>Courier</th>
                                        <th>Dispatch Date</th>
                                        <th>Confirm Status</th>
                                        <th>Shipping Status</th>
                                        <th>Order Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading
                                        ? [...Array(10)].map((_, i) => <SkeletonRow key={i} />)
                                        : records.length === 0
                                            ? <tr>
                                                <td colSpan={13} className="text-center py-5" style={{ color: '#bbb' }}>No orders found</td></tr>
                                            : records.map((o, i) => {
                                                const ss = getStatusStyle(o.shipping_status);
                                                return (
                                                    <tr key={i}>
                                                        <td style={{ color: '#aaa', fontSize: 12 }}>{(currentPage - 1) * perPage + i + 1}</td>
                                                        <td>
                                                            <span style={{ fontSize: 12, fontWeight: 700, color: '#4e9191', fontFamily: 'monospace' }}>
                                                                #{o.order_number}
                                                            </span>
                                                        </td>
                                                        <td style={{ fontFamily: 'monospace', fontSize: 12, color: '#888' }}>
                                                            {o.awb_number || '—'}
                                                        </td>
                                                        <td style={{ fontWeight: 500 }}>{o.order_name}</td>
                                                        <td>{o.order_mobile}</td>
                                                        <td>{o.order_state}</td>
                                                        <td>{o.order_city}</td>
                                                        <td>{o.order_pincode}</td>
                                                        <td style={{ fontSize: 12 }}>{o.ocourier_name || '—'}</td>
                                                        <td style={{ fontSize: 12 }}>{o.dispatch_date || '—'}</td>
                                                        <td>
                                                            <span style={{
                                                                background: o.order_confirm_status === 'Confirm Order' ? '#e8f5e9' : '#fff3e0',
                                                                color: o.order_confirm_status === 'Confirm Order' ? '#0e7c52' : '#e65100',
                                                                padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600
                                                            }}>
                                                                {o.order_confirm_status || '—'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span style={{ background: ss.bg, color: ss.color, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                                                                {o.shipping_status || 'New'}
                                                            </span>
                                                        </td>
                                                        <td style={{ fontSize: 12, color: '#888' }}>
                                                            {o.order_date ? o.order_date.split(' ')[0] : '—'}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                    }
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="d-flex justify-content-end p-3">
                            <nav>
                                <ul className="pagination round-pagination mb-0">
                                    <li className="page-item">
                                        <a href="#" className="page-link" onClick={e => { e.preventDefault(); if (currentPage > 1) setPage(currentPage - 1); }}>← Prev</a>
                                    </li>
                                    {numbers.slice(Math.max(0, currentPage - 2), Math.max(5, currentPage + 3)).map((n, i) => (
                                        <li key={i} className={`page-item ${currentPage === n ? 'actives' : ''}`}>
                                            <a href="#" className="page-link" onClick={e => { e.preventDefault(); setPage(n); }}>{n}</a>
                                        </li>
                                    ))}
                                    <li className="page-item">
                                        <a href="#" className="page-link" onClick={e => { e.preventDefault(); if (currentPage < npage) setPage(currentPage + 1); }}>Next →</a>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </div>
                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(SkinrangeOrders);